// Persistent offline sync queue. Every change made offline is stored here
// (survives app restarts) and replayed automatically when connectivity returns.
import axios from "axios";
import { API } from "@/lib/api";

const KEY = "sync_queue";

const read = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
};
const write = (q) => {
  try { localStorage.setItem(KEY, JSON.stringify(q)); } catch {}
  window.dispatchEvent(new CustomEvent("sync-queue-changed", { detail: { count: q.length } }));
};

// op: { method: 'post'|'put'|'delete', path: '/orders/xyz', body, entity: 'orders', entityId }
export const queueOp = (op) => {
  const q = read();
  q.push({ ...op, ts: Date.now(), opId: (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`) });
  write(q);
};

export const queueCount = () => read().length;

// Write-ahead: persist the op FIRST (survives app close / network hangs), then
// execute immediately when online. The op is only removed after the server confirms.
export const queueAndFlush = (op) => {
  queueOp(op);
  flushQueue();
};

// IDs deleted locally but not yet confirmed by the server — used so a
// background refetch never resurrects a record the user already deleted
export const getQueuedDeletedIds = (entity) =>
  read().filter((o) => o.method === "delete" && o.entity === entity).map((o) => o.entityId);

// Records created locally but not yet on the server — merged into fetch results
export const getQueuedCreates = (entity) =>
  read().filter((o) => o.method === "post" && o.entity === entity).map((o) => o.body);

// All queued ops for an entity (e.g. to hide orders with a pending "completed" update)
export const getQueuedOps = (entity) => read().filter((o) => o.entity === entity);

let flushing = false;
let rerunRequested = false;
const opKey = (op) => op.opId || `${op.ts}|${op.method}|${op.path}`;
export async function flushQueue() {
  if (!navigator.onLine) return;
  if (flushing) { rerunRequested = true; return; } // ops queued mid-flight run right after
  const q = read();
  if (q.length === 0) return;
  flushing = true;
  window.dispatchEvent(new CustomEvent("sync-queue-syncing"));
  const completed = new Set(); // ops confirmed by the server (or permanently dropped)
  const attemptsByKey = new Map();
  const blockedIds = new Set(); // keep per-record ordering: if one op fails, later ops for the same record wait
  let newQ = q;
  try {
    for (const op of q) {
      if (op.entityId && blockedIds.has(`${op.entity}:${op.entityId}`)) continue;
      try {
        const url = `${API}${op.path}`;
        const cfg = { timeout: 15000 }; // hung requests fail fast and retry — never lost
        if (op.method === "post") await axios.post(url, op.body, cfg);
        else if (op.method === "put") await axios.put(url, op.body, cfg);
        else if (op.method === "delete") await axios.delete(url, cfg);
        completed.add(opKey(op));
      } catch (err) {
        const status = err.response?.status;
        // 404 on delete = already gone — safe to drop
        if (status === 404) { completed.add(opKey(op)); continue; }
        // Server rejected the payload: retry a few times (a fix may deploy), then
        // surface it loudly instead of silently losing the user's change
        if (status === 400 || status === 422) {
          const attempts = (op.attempts || 0) + 1;
          if (attempts >= 8) {
            console.error("Sync op permanently rejected, dropping:", op);
            completed.add(opKey(op));
            continue;
          }
          attemptsByKey.set(opKey(op), attempts);
        }
        if (op.entityId) blockedIds.add(`${op.entity}:${op.entityId}`);
      }
    }
    // Re-read the queue: ops queued WHILE this flush ran must survive — only remove
    // the ops this flush actually completed (a full overwrite would erase them)
    const current = read();
    newQ = current
      .filter((op) => !completed.has(opKey(op)))
      .map((op) => (attemptsByKey.has(opKey(op)) ? { ...op, attempts: attemptsByKey.get(opKey(op)) } : op));
    write(newQ);
  } finally {
    // ALWAYS reset — an unexpected error here must never deadlock the queue
    // (a stuck `flushing` flag froze the sync badge/spinner until app restart)
    flushing = false;
    window.dispatchEvent(new CustomEvent("sync-queue-flushed", { detail: { remaining: newQ.length, flushed: q.length - newQ.length } }));
  }
  // Ops that arrived mid-flight (e.g. rapid deletes) are flushed immediately
  const snapshotKeys = new Set(q.map(opKey));
  const hasNewOps = newQ.some((op) => !snapshotKeys.has(opKey(op)));
  if (rerunRequested || hasNewOps) { rerunRequested = false; flushQueue(); }
}

// Automatic sync: on reconnect, on app open, and as a steady retry fallback
window.addEventListener("online", () => setTimeout(flushQueue, 500));
setInterval(flushQueue, 10000);
setTimeout(flushQueue, 2000);
